import { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";
import { QuoteProvider, useQuote } from "@/lib/quote";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import QuoteDrawer from "@/components/QuoteDrawer";
import MobileQuoteBar, { MobileQuoteSpacer } from "@/components/MobileQuoteBar";
import PaletteAtelier from "@/components/PaletteAtelier";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ShopItem from "@/pages/ShopItem";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Collections from "@/pages/Collections";
import CollectionDetail from "@/pages/CollectionDetail";
import About from "@/pages/About";
import Gallery from "@/pages/Gallery";
import ServiceArea from "@/pages/ServiceArea";
import QuotePage from "@/pages/Quote";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Admin from "@/pages/Admin";
import NotFound from "@/pages/NotFound";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-ivory px-6 text-center">
          <div>
            <p className="font-display text-3xl text-ink">Something went sideways.</p>
            <button onClick={() => window.location.reload()} className="mt-6 rounded-full bg-olive px-8 py-3 text-[12px] uppercase tracking-[0.18em] text-ivory" data-testid="error-reload-button">
              Refresh the page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
}

function Shell() {
  const { count } = useQuote();
  return (
    <div className="grain min-h-screen bg-ivory text-ink">
      <SmoothScroll />
      <ScrollToTop />
      <Nav />
      <main className={count > 0 ? "pb-[64px] lg:pb-0" : ""}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/shop/:slug" element={<ShopItem />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/collections" element={<Collections />} />
          <Route path="/collections/:slug" element={<CollectionDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/service-area" element={<ServiceArea />} />
          <Route path="/quote" element={<QuotePage />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <MobileQuoteSpacer />
      <QuoteDrawer />
      <MobileQuoteBar />
      <PaletteAtelier />
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <QuoteProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </QuoteProvider>
    </ErrorBoundary>
  );
}
